import uniq from "lodash/uniq";
import {
	authInterceptorFactory,
	axios,
	interceptAndNormalizeResponse,
	requestHeaderNormalizerInterceptor,
	piggyBack
} from "@teaming/io-helpers";
import {
	CommitmentFilterType,
	OutcomeSortType,
	OutcomeStatus,
	OutcomeType
} from "@teaming/common-enums";
import { upcat } from "@teaming/common-util";
import * as outcomeHelpers from "./outcomeHelpers";

const IO_OUTCOME_NAMESPACE = "outcome";

export default function ioOutcomeFactory( { config: { baseUri, }, authService, } ) {
	axios.defaults.baseURL = baseUri;
	axios.interceptors.request.use( requestHeaderNormalizerInterceptor );
	axios.interceptors.request.use( authInterceptorFactory( { authService, } ) );
	axios.interceptors.response.use( ...interceptAndNormalizeResponse() );

	const outcomeMethods = {
		fetchOutcome: piggyBack( async ( {
			outcomeId,
		} ) => {
			const url = `/outcome/${ outcomeId }`;
			const response = await axios.get( url );

			response.data = outcomeHelpers.toClientOutcome( response.data );
			return response;
		}, { timeout: 100, } ),

		fetchCommitments: piggyBack( async ( {
			teamId,
			status,
			personId, // this is for filtering to outcomes assigned to this user
			sortType,
			filterType,
			offset,
			limit,
			outcomeType = [],
		} ) => {
			const url = "/outcome";

			const mergedOutcomeType = uniq( [
				OutcomeType.ACTION_ITEM,
				...outcomeType,
			] );

			const unyieldingOptions = {
				outcomeType: mergedOutcomeType.join( "," ),
			};

			const params = Object.assign( {
				personId,
				teamId,
				status: status || OutcomeStatus.ALL,
				sortType: sortType || OutcomeSortType.OUTCOME_IMPORTANCE,
				filterType: filterType || CommitmentFilterType.OUTSTANDING,
				offset: offset || 0,
				// eslint-disable-next-line no-magic-numbers
				limit: limit || 10,
			}, unyieldingOptions );

			const response = await axios.get( url, { params, } );
			response.data = response.data.map( commitment => {
				const clientCommitment = outcomeHelpers.toClientOutcome( commitment );
				// safeguard: ensure that any filter type that was used in the API request
				// is included in the matched filter types (in theory it should be already but...)
				if ( filterType ) {
					clientCommitment.matchedFilters = upcat(
						clientCommitment.matchedFilters,
						[ filterType, ]
					);
				}
				return clientCommitment;
			} );

			return response;
		}, { timeout: 100, } ),

		fetchCommitmentMetadata: piggyBack( async params => {
			let url = "/commitment-metadata";
			if ( params?.teamId ) {
				url += `?teamId=${ params.teamId }`;
			}
			return await axios.get( url );
		}, { timeout: 100, } ),

		async createCommitment( { commitment, } ) {
			if ( !commitment.teamId && !commitment.assignments.length ) {
				throw new Error( "a commitment must belong to a team, be assigned to an individual, or both" );
			}
			delete commitment.id;
			const url = "/outcome";
			const apiVersion = 3;
			const headers = {
				"x-api-version": apiVersion,
			};
			const apiCommitment = outcomeHelpers.toAPIOutcome( commitment );
			const response = await axios.post( url, apiCommitment, { headers, } );
			response.data = outcomeHelpers.toClientOutcome( response.data );
			return response;
		},

		async updateCommitment( { commitment, } ) {
			const url = `/outcome/${ commitment.id }`;
			const apiVersion = 2;
			const headers = {
				"x-api-version": apiVersion,
			};
			const apiCommitment = outcomeHelpers.toAPIOutcome( commitment );
			const response = await axios.put( url, apiCommitment, { headers, } );
			response.data = outcomeHelpers.toClientOutcome( response.data );
			return response;
		},

		async deleteOutcome( { outcomeId, } ) {
			const url = `/outcome/${ outcomeId }`;
			const response = await axios( {
				url,
				method: "delete",
			} );
			return response;
		},
	};

	return {
		namespace: IO_OUTCOME_NAMESPACE,
		...outcomeMethods,
	};
}
